import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUserCompanySeller, generateTokenByCompanySeller, saveUser, showFullLoader } from '../../store/actions/auth';
import { setLocalStorageItem } from '../../services/StorageService';
import { initiateWalmartOAuthWithParams } from '../../services/walmart.service';
import './WalmartSelectSeller.css';

/**
 * WalmartSelectSeller - Page for selecting which seller account to connect to Walmart
 * Used when user has multiple company/seller associations
 */
const WalmartSelectSeller = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const { companyList, user } = useSelector((state) => state.auth);
    const { isLoggedIn } = useSelector((state) => state.auth);

    useEffect(() => {
        // Redirect to login if not logged in
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }

        // Check if there's a pending Walmart connection
        const pendingConnect = sessionStorage.getItem('walmart_pending_connect');
        if (pendingConnect !== 'true') {
            // No pending Walmart connection - redirect to home
            navigate('/');
            return;
        }

        // Fetch company/seller list if not already loaded
        if (!companyList || companyList.length === 0) {
            dispatch(getUserCompanySeller());
        }
    }, [isLoggedIn, dispatch, navigate]);

    // Auto-proceed with the first seller
    useEffect(() => {
        if (companyList?.length >= 1) {
            handleSelectSeller(companyList[0]);
        }
    }, [companyList]);

    const handleSelectSeller = async (seller) => {
        setLoading(true);
        setError(null);
        setSelectedSeller(seller);

        try {
            // Generate token for selected company/seller
            const payload = {
                ...seller,
                companyId: seller.companyId,
                sellerId: seller.sellerId,
                emailId: user?.Value?.emailId,
            };

            const tokenRes = await Promise.resolve(dispatch(generateTokenByCompanySeller(payload)));
            
            if (tokenRes.data) {
                // Save the selected seller context
                setLocalStorageItem('userDetails', JSON.stringify(tokenRes.data));
                setLocalStorageItem('currentCompany', JSON.stringify(seller));
                dispatch(saveUser(tokenRes.data));

                // Now proceed to Walmart OAuth
                proceedToWalmartOAuth();
            } else {
                setError('Failed to switch to selected seller. Please try again.');
                setLoading(false);
            }
        } catch (err) {
            console.error('Error selecting seller:', err);
            setError('An error occurred while selecting the seller. Please try again.');
            setLoading(false);
        }
    };

    const proceedToWalmartOAuth = () => {
        const walmartCallbackUri = sessionStorage.getItem('walmart_callback_uri');
        const clientType = sessionStorage.getItem('walmart_client_type') || 'seller';

        // Clear pending flags
        sessionStorage.removeItem('walmart_pending_connect');
        sessionStorage.removeItem('walmart_callback_uri');
        sessionStorage.removeItem('walmart_client_type');
        sessionStorage.removeItem('walmart_needs_seller_selection');

        // Redirect to Walmart OAuth
        initiateWalmartOAuthWithParams(walmartCallbackUri, clientType);
    };

    const handleCancel = () => {
        // Clear Walmart session data
        sessionStorage.removeItem('walmart_pending_connect');
        sessionStorage.removeItem('walmart_callback_uri');
        sessionStorage.removeItem('walmart_client_type');
        sessionStorage.removeItem('walmart_needs_seller_selection');
        
        navigate('/');
    };

    if (!isLoggedIn) {
        return null;
    }

    return (
        <div className="walmart-select-seller-container">
            <div className="walmart-select-seller-card">
                <div className="walmart-select-seller-header">
                    <img 
                        src="https://upload.wikimedia.org/wikipedia/commons/c/ca/Walmart_logo.svg" 
                        alt="Walmart" 
                        className="walmart-logo"
                    />
                    <h2>Connect to Walmart</h2>
                    <p className="subtitle">Select which seller account you want to connect with Walmart</p>
                </div>

                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="loading-state">
                        <span className="spinner-border spinner-border-lg"></span>
                        <p>Connecting to Walmart...</p>
                    </div>
                ) : (
                    <>
                        <div className="seller-list">
                            {companyList?.length === 0 ? (
                                <div className="no-sellers">
                                    <p>No seller accounts found.</p>
                                    <p>Please contact your administrator to set up a seller account.</p>
                                </div>
                            ) : (
                                companyList?.map((seller) => (
                                    <button 
                                        type="button"
                                        key={seller.companySellerId}
                                        className={`seller-item ${selectedSeller?.companySellerId === seller.companySellerId ? 'selected' : ''}`}
                                        onClick={() => !loading && setSelectedSeller(seller)}
                                        disabled={loading}
                                    >
                                        <div className="seller-info">
                                            <div className="seller-name">{seller.companySellerName}</div>
                                            <div className="seller-details">
                                                {seller.companyName && <span>Company: {seller.companyName}</span>}
                                            </div>
                                        </div>
                                        <div className="seller-radio">
                                            <input 
                                                type="radio" 
                                                name="seller" 
                                                checked={selectedSeller?.companySellerId === seller.companySellerId}
                                                onChange={() => setSelectedSeller(seller)}
                                                disabled={loading}
                                            />
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>

                        <div className="action-buttons">
                            <button 
                                className="btn btn-secondary"
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary walmart-btn"
                                onClick={() => selectedSeller && handleSelectSeller(selectedSeller)}
                                disabled={!selectedSeller || loading}
                            >
                                Connect with Walmart
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default WalmartSelectSeller;
